import { useEffect } from 'react'

export interface DialogKeyboardShortcutsOptions {
  isOpen: boolean
  onCancel: () => void
  onConfirm?: () => void
  /** Skip Enter-to-confirm while focus is in a text field (default true). */
  skipConfirmWhenTyping?: boolean
  confirmDisabled?: boolean
  cancelDisabled?: boolean
}

export function isDialogTextInput(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (tag === 'INPUT') {
    const type = (target as HTMLInputElement).type
    return type !== 'button' && type !== 'submit' && type !== 'checkbox' && type !== 'radio'
  }
  return target.isContentEditable
}

export function useDialogKeyboardShortcuts({
  isOpen,
  onCancel,
  onConfirm,
  skipConfirmWhenTyping = true,
  confirmDisabled = false,
  cancelDisabled = false,
}: DialogKeyboardShortcutsOptions): void {
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (cancelDisabled) return
        event.preventDefault()
        event.stopPropagation()
        onCancel()
        return
      }

      if (event.key !== 'Enter' || !onConfirm || confirmDisabled) return
      if (skipConfirmWhenTyping && isDialogTextInput(event.target)) return

      event.preventDefault()
      event.stopPropagation()
      onConfirm()
    }

    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [isOpen, onCancel, onConfirm, skipConfirmWhenTyping, confirmDisabled, cancelDisabled])
}
